import React, { useState } from 'react';
import { Button, message } from 'antd';

interface XunhupayButtonProps {
  product?: 'hepan' | 'career' | 'vedic';
  reportToken?: string;
  onSuccess: (tradeNo: string) => void;
  onError?: (error: string) => void;
  buttonText?: string;
  style?: React.CSSProperties;
  className?: string;
  compressedReport?: string; // 压缩后的报告数据
  onOrderCreated?: (tradeNo: string) => void;
}

/**
 * 虎皮椒支付按钮组件
 * 集成微信/支付宝支付
 */
const XunhupayButton: React.FC<XunhupayButtonProps> = ({
  onSuccess,
  onError,
  buttonText = '立即解锁',
  style = {},
  className,
  compressedReport,
  product = 'hepan',
  reportToken,
  onOrderCreated,
}) => {
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    setLoading(true);
    try {
      // 调用后端创建订单 API
      const response = await fetch('/api/xunhupay-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product,
          type: 'wechat', // 默认微信支付，可改为 'alipay'
          reportToken,
          compressedReport, // 传递压缩后的报告数据
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || '创建订单失败');
      }

      // 保存订单号
      const tradeNo = data.tradeNo;
      onOrderCreated?.(tradeNo);
      
      // 打开支付弹窗
      if (data.payUrl) {
        // 方式1：跳转支付链接（手机端使用当前页跳转，避免弹窗拦截）
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          // 手机端：当前页跳转
          window.location.href = data.payUrl;
        } else {
          // PC端：新标签页打开
          const payWindow = window.open(data.payUrl, '_blank');
          if (!payWindow) {
            message.warning('请允许弹窗，或检查是否被浏览器拦截');
          }
        }
      } else if (data.qrCode) {
        // 方式3：展示二维码
        showQRCodeModal(data.qrCode, tradeNo);
      } else {
        throw new Error('未获取到支付链接');
      }

      // 开始轮询支付状态
      startPolling(tradeNo);
      
    } catch (error: any) {
      console.error('支付初始化失败:', error);
      message.error(error.message || '支付初始化失败，请稍后重试');
      onError?.(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 轮询支付状态
  const startPolling = (tradeNo: string) => {
    let pollCount = 0;
    const maxPolls = 60; // 最多轮询60次（5分钟）
    
    const poll = async () => {
      try {
        const response = await fetch(`/api/xunhupay-query?tradeNo=${tradeNo}`);
        const data = await response.json();
        
        if (data.status === 'SUCCESS') {
          message.success('支付成功！');
          onSuccess(tradeNo);
          return;
        } else if (data.status === 'FAILED') {
          message.error('支付失败，请重试');
          return;
        }
        
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, 5000); // 每5秒轮询一次
        }
      } catch (error) {
        console.error('查询支付状态失败:', error);
      }
    };
    
    poll();
  };

  // 展示二维码弹窗
  const showQRCodeModal = (qrCode: string, tradeNo: string) => {
    // 这里可以实现一个 Modal 展示二维码
    // 简化处理：直接打开新窗口
    const qrWindow = window.open('', '_blank', 'width=400,height=500');
    if (qrWindow) {
      qrWindow.document.write(`
        <html>
          <head><title>扫码支付</title></head>
          <body style="text-align:center;padding:40px;font-family:sans-serif;">
            <h2>请使用微信扫码支付</h2>
            <img src="${qrCode}" style="width:250px;height:250px;margin:20px;"/>
            <p>订单号：${tradeNo}</p>
            <p style="color:#999;font-size:12px;">支付完成后请关闭此窗口</p>
          </body>
        </html>
      `);
    }
  };

  return (
    <Button
      type="primary"
      size="large"
      loading={loading}
      onClick={handlePay}
      className={className}
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        height: '48px',
        fontSize: '16px',
        fontWeight: 600,
        ...style
      }}
    >
      {buttonText}
    </Button>
  );
};

export default XunhupayButton;
